import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, GripVertical, Maximize2, Minimize2 } from 'lucide-react';
import { WidgetProps } from '@/lib/widgets/types';
import { widgetRegistry } from '@/lib/widgets/registry';

interface WidgetContainerProps extends WidgetProps {
  onRemove?: () => void;
  onResize?: (newSize: { w: number; h: number }) => void;
  isDragging?: boolean;
}

export function WidgetContainer({ 
  id, 
  config, 
  isEditing = false, 
  onRemove,
  onResize,
  isDragging = false
}: WidgetContainerProps) {
  const widgetDef = widgetRegistry.getWidget(config.type);
  
  if (!widgetDef) {
    return (
      <Card className="p-4 border-dashed border-destructive">
        <p className="text-sm text-muted-foreground">Widget "{config.type}" não encontrado</p>
      </Card>
    );
  }

  const WidgetComponent = widgetDef.component;

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.();
  };

  return (
    <Card className={`
      relative h-full overflow-hidden transition-shadow
      ${isDragging ? 'shadow-lg ring-2 ring-primary' : ''}
      ${isEditing ? 'ring-1 ring-muted pointer-events-none' : ''}
    `}>
      {isEditing && (
        <div className="absolute top-1 right-1 z-20 flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 opacity-70 hover:opacity-100 pointer-events-auto bg-background/80 hover:bg-background shadow-sm widget-remove-button"
            onMouseDown={handleRemoveClick}
            onClick={handleRemoveClick}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
      
      {isEditing && (
        <div className="absolute top-1 left-1 z-20 cursor-move opacity-60 hover:opacity-100 pointer-events-auto">
          <div className="p-1 bg-background/60 rounded">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      )}

      <div className={`h-full ${isEditing ? 'pt-8 pointer-events-auto' : ''} widget-content`}>
        <WidgetComponent id={id} config={config} isEditing={isEditing} />
      </div>
    </Card>
  );
}